import React, { useState } from "react";
import { useSelector } from "react-redux";
import { generateAccessCode, axiosInstance, formatPhoneNumberWa, decodeAtob } from "../../../utils/helper";
import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import { useHistory } from "react-router-dom";
import { WhatsAppIcon } from "../../../utils/svg";
import { DevicePhoneMobileIcon, XMarkIcon } from '@heroicons/react/24/outline';
import PhoneInput from "../../components/Auth/PhoneInput";
import OtpInput from "../../components/Auth/OtpInput";
import { BottomSheet } from "react-spring-bottom-sheet";
import { showError } from "../../hook/useError";
import { apiUrlB2B, GEOLOCATION_PERMISSION, UFOODX_BASE_URL } from '../../../utils/constants';
import { useDispatch } from 'react-redux';
import { setUser } from '../../../store/reducers/user';
import { setCompany } from "../../../store/reducers/ufood-x/company";
import useNavigation from '../../../hook/useNavigation';

const Login = () => {
    const dispatch = useDispatch();
    const navigate = useNavigation();
    const history = useHistory();

    const { details: outlet } = useSelector((store) => store?.outlet);
    const company = useSelector((store) => store?.company);
    const user = useSelector((store) => store?.user);

    const [modalStep, setModalStep] = useState(null);
    const [phone, setPhone] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const handleWhatsApp = async () => {
        try {
            const companyId = company?.companyID
            const brandId = company?.brandId;
            const outletId = outlet?.storeID;

            const outletName = outlet?.storeName;

            const accessCode = generateAccessCode(companyId, brandId, outletId, process.env.REACT_APP_NODE_ENV);
            const message = encodeURIComponent(
                `Halo dari Ufood! 👋
Saya mau login ke ${outletName}

🔑 Kode akses saya: ${accessCode}

Langsung kirim chat ini ya, jangan diubah!`
            );
            const phone = '628119527701'

            window.location.href = `https://wa.me/${phone}?text=${message}`;
        } catch (err) {
            console.log("err", err);
        }
    };

    const handleSms = async () => {
        setModalStep("phone");
    };

    const handleGetOtp = async (phoneNumber) => {
        try {
            setIsLoading(true);
            const payload = {
                phoneNumber: '62' + phoneNumber,
                companyId: Number(company?.companyID),
                lastPage: window.location.pathname,
                loginMethod: "sms",
            }
            const res = await axiosInstance.post(`${apiUrlB2B}auth-ufoodx/login`, payload);
            if (res?.data?.statusCode !== 200) {
                return showError({
                    title: res?.data?.message,
                });
            }
            setPhone(phoneNumber);
            return setModalStep("otp");
        } catch (err) {
            showError({
                title:
                    err?.response?.data?.message ||
                    err?.message ||
                    "Gagal meminta OTP",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleResend = async () => {
        try {
            const payload = {
                phoneNumber: '62' + phone,
                companyId: Number(company?.companyID),
            }
            await axiosInstance.post(`${apiUrlB2B}auth-ufoodx/resend-otp`, payload);
        } catch (err) {
            showError({
                title:
                    err?.response?.data?.message ||
                    err?.message ||
                    "Gagal meminta OTP",
            });
        }
    };

    const handleLogin = async (otpCode) => {
        try {
            const payload = {
                phoneNumber: '62' + phone,
                otp: Number(otpCode),
            }
            const res = await axiosInstance.post(`${apiUrlB2B}auth-ufoodx/verify-otp`, payload);
            if (res?.data?.statusCode === 200) {
                const { token, refreshToken, customerData } = res?.data?.data;

                const dataCustomer = decodeAtob(customerData);
                const parseCustomer = JSON.parse(dataCustomer);

                const userData = {
                    firstName: parseCustomer?.firstName,
                    lastName: parseCustomer?.lastName,
                    email: parseCustomer?.email,
                    primary_phone: parseCustomer?.primary_phone,
                    customerID: parseCustomer?.customerID,
                    companyID: parseCustomer?.companyID,
                    companyName: parseCustomer?.companyName,
                    paymentMethod: parseCustomer?.paymentMethod?.toLowerCase(),
                    birthDate: parseCustomer?.birthDate,
                    gender: parseCustomer?.customerGender,
                    status: parseCustomer?.customerStatus || 'P',
                };
                dispatch(
                    setUser({
                        ...userData,
                        token: token,
                        refresh_token: refreshToken,
                        is_b2b: true,
                        device: null,
                        location_permission: GEOLOCATION_PERMISSION.PROMPT,
                        permission: null,
                    }),
                );

                if (Boolean(parseCustomer?.loyalty)) {
                    const companyData = {
                        companyLoyaltyPassword: parseCustomer?.companyLoyaltyPassword,
                        companyLoyaltyUrl: parseCustomer?.companyLoyaltyUrl,
                        companyLoyaltyUsername: parseCustomer?.companyLoyaltyUsername,
                        loyalty: parseCustomer?.loyalty,
                        companyLoyaltyWidgetUrl: parseCustomer?.companyLoyaltyWidgetUrl,
                        companyLoyaltyClientCode: parseCustomer?.companyLoyaltyClientCode,
                    };
                    dispatch(setCompany(companyData));
                }

                return navigate.push(UFOODX_BASE_URL);
            }
        } catch (err) {
            showError({
                title:
                    err?.response?.data?.message ||
                    err?.message ||
                    "Gagal meminta OTP",
            });
        }
        setModalStep(null);
    };

    const handleCloseModal = () => {
        setModalStep(null);
        setPhone("");
    };

    const handleModalBack = () => {
        if (modalStep === "otp") setModalStep("phone");
        else handleCloseModal();
    };

    return (
        <div className="min-h-screen flex flex-col max-w-3xl mx-auto">
            <div className="px-4 py-4 border-b-1 border-solid border-gray-200 flex items-center" onClick={() => history.goBack()}>
                <ArrowLeftIcon className="h-6 w-6 text-gray-900 cursor-pointer" />
                <span className="ml-2 font-medium text-gray-900">
                    Login
                </span>
            </div>

            <div className="px-4 pt-10">
                <img
                    src={company?.companyLogo}
                    alt="Logo"
                    className="w-24 mx-auto mb-6"
                />
            </div>

            <div className="flex-1 px-6 pb-10 flex flex-col gap-0 items-center justify-center">
                <button
                    onClick={handleWhatsApp}
                    className="w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 active:bg-green-700 text-white font-semibold py-3.5 rounded-xl transition-colors border-0 text-sm"
                >
                    <WhatsAppIcon className="w-5 h-5" />
                    Login or Register with WhatsApp
                </button>

                <div className="flex items-center gap-3 py-4 w-full">
                    <div className="flex-1 border-t border-solid border-gray-300" />
                    <span className="text-xs text-gray-400 whitespace-nowrap">or login with</span>
                    <div className="flex-1 border-t border-solid border-gray-300" />
                </div>

                <button
                    onClick={handleSms}
                    className="w-full flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-900 active:bg-black text-white font-semibold py-3.5 rounded-xl transition-colors border-0 text-sm"
                >
                    <DevicePhoneMobileIcon className="w-5 h-5" />
                    Login or Register with Phone
                </button>
            </div>

            <div className="mt-auto pb-4 text-xs text-gray-500 text-center">
                <span>Powered by</span>
                <img
                    src="../../../../assets/img/ufood-x/logo-ufoodx.webp"
                    alt="UFOODX"
                    className="mx-auto mt-2 h-6"
                />
            </div>

            <BottomSheet
                blocking={false}
                key="login-phone-otp"
                open={!!modalStep}
                onDismiss={handleCloseModal}
                header={false}
                snapPoints={({ maxHeight }) => [maxHeight, maxHeight]}
            >
                <div className="px-6 pt-2 pb-8">
                    <div className="flex items-center justify-between mb-6">
                        {modalStep === "otp" ? (
                            <button
                                onClick={handleModalBack}
                                className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 border-0 cursor-pointer"
                            >
                                <ArrowLeftIcon className="w-4 h-4 text-gray-700" />
                            </button>
                        ) : (
                            <div />
                        )}
                        <button
                            onClick={handleCloseModal}
                            className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 border-0 cursor-pointer"
                        >
                            <XMarkIcon className="w-4 h-4 text-gray-700" />
                        </button>
                    </div>

                    {modalStep === "phone" && (
                        <PhoneInput onGetOtp={handleGetOtp} isLoading={isLoading} />
                    )}
                    {modalStep === "otp" && (
                        <OtpInput phone={phone} handleResend={handleResend} onLogin={handleLogin} />
                    )}

                </div>
            </BottomSheet>
        </div >
    );
};

export default Login;
